import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiZap, FiAward, FiChevronRight } from 'react-icons/fi';
import challengeBolt from '@/assets/images/icons/challenge-bolt-3d.png';
import toast from 'react-hot-toast';
import { liveChallengesApi } from '@/services/liveChallenges';
import { useLiveChallenge } from '@/hooks/useLiveChallenge';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { PATHS } from '@/routes/paths';
import { Button } from '@/components/ui/button';
import { Loader } from '@/components/custom/loader';
import { Header } from '@/components/custom/header/header';
import { BottomBarInfantil } from '@/components/custom/bottom-bar-infantil';
import tiaUau from '@/assets/images/tia/TIA-UAU.png';
import Breadcrumbs from '@/components/custom/breadcrumbs/breadcrumbs';
import { TypewriterText } from '@/components/custom/typewriter-text';

const normalizeParticipation = item => ({
  id: item.id,
  ...Object.fromEntries(Object.entries(item.attributes || {}).map(([k, v]) => [k.replace(/-/g, '_'), v]))
});

const formatDate = value => (value ? new Date(value).toLocaleDateString('pt-BR') : '—');

// Painel "Meus desafios": cards das participações do aluno (histórico), ao lado
// do acesso por código. Reaproveita liveChallengesApi.participations.
function ParticipationsPanel() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null); // null = carregando
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    let active = true;
    liveChallengesApi
      .participations(1)
      .then(response => {
        if (!active) return;
        setItems((response?.data ?? []).map(normalizeParticipation));
        setHasMore((response?.meta?.total_pages ?? 1) > 1);
      })
      .catch(() => {
        if (active) setItems([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const isPodium = rank => rank != null && rank <= 3;

  return (
    <section
      className="flex min-w-0 flex-col rounded-[24px] p-5 shadow-card"
      style={{ background: 'var(--color-bg-lilac)' }}
    >
      <div className="mb-4 flex items-center gap-2">
        <FiAward className="h-5 w-5 text-secondary" />
        <h2 className="m-0 text-base font-extrabold text-foreground">Meus desafios</h2>
      </div>

      {items === null ? (
        <div className="grid flex-1 place-items-center py-8">
          <Loader />
        </div>
      ) : items.length === 0 ? (
        <div className="border-secondary/30 grid flex-1 place-items-center rounded-2xl border-2 border-dashed bg-white/60 px-4 py-10 text-center">
          <p className="m-0 text-sm font-semibold text-neutral">
            Você ainda não participou de nenhum desafio ao vivo.
            <br />
            Entre em um com o código ao lado! ⚡
          </p>
        </div>
      ) : (
        <ul className="m-0 grid list-none grid-cols-1 gap-3 p-0 sm:grid-cols-2">
          {items.map(p => {
            const podium = isPodium(p.rank);
            return (
              <li key={p.id}>
                <div className="group flex h-full items-center gap-3 rounded-2xl border-2 border-white bg-white p-4 shadow-card transition-transform duration-150 hover:-translate-y-0.5">
                  {/* Ícone 3D do tema Desafio ao Vivo (raio ⚡), no estilo dos cards da home */}
                  <img
                    src={challengeBolt}
                    alt=""
                    aria-hidden="true"
                    className="h-14 w-14 shrink-0 object-contain"
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="m-0 truncate text-sm font-extrabold text-neutral-dark">{p.challenge_title}</h3>
                      {p.rank != null && (
                        <span
                          className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-extrabold"
                          style={podium ? { background: 'color-mix(in srgb, var(--color-secondary) 18%, #fff)', color: 'var(--color-secondary-dark)' } : { background: 'var(--color-neutral-lightest)', color: 'var(--color-neutral)' }}
                        >
                          {p.rank}º
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-xs text-neutral">
                      {formatDate(p.activation_started_at)} · {p.host_name || '—'}
                    </p>
                    <span
                      className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-extrabold text-primary-dark"
                      style={{ background: 'color-mix(in srgb, var(--color-primary) 12%, #fff)' }}
                    >
                      <FiZap className="h-3 w-3" />
                      {p.total_score ?? 0} pts
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && (
        <button
          type="button"
          onClick={() => navigate(PATHS.LIVE_CHALLENGE.MY_CHALLENGES)}
          className="mt-4 inline-flex items-center justify-center gap-1 self-center text-sm font-extrabold text-primary transition-opacity hover:opacity-80"
        >
          Ver todos <FiChevronRight className="h-4 w-4" />
        </button>
      )}
    </section>
  );
}

/**
 * JoinPage - Entry point for students joining a live challenge.
 *
 * Reads an optional `code` from the URL search params (e.g. from a QR code).
 * If the code is present it auto-submits; otherwise the student types the
 * access code manually.
 */
const JoinPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setSession, subscribe } = useLiveChallenge();

  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoJoinAttempted, setAutoJoinAttempted] = useState(false);

  const handleJoin = useCallback(
    async accessCode => {
      if (!accessCode || loading) return;

      setLoading(true);
      try {
        const response = await liveChallengesApi.join(accessCode);
        const sessionData = response?.data ?? response;

        setSession(sessionData);
        subscribe(sessionData.activation_id, 'student');
        navigate(`/desafio-ao-vivo/${sessionData.activation_id}/aguardando`);
      } catch (error) {
        const message = error?.response?.data?.error || error?.response?.data?.message || 'Código inválido ou desafio não encontrado.';
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [loading, navigate, setSession, subscribe]
  );

  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode && !autoJoinAttempted) {
      setAutoJoinAttempted(true);
      setCode(urlCode);
      handleJoin(urlCode);
    }
  }, [searchParams, autoJoinAttempted, handleJoin]);

  const handleSubmit = e => {
    e.preventDefault();
    handleJoin(code.trim());
  };

  const playful = usePlayfulStudent();

  const joinHeaderBlock = (
    <header className="mb-6 flex flex-col items-center gap-3">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-dropshadow-md">
        <FiZap className="h-7 w-7" />
      </div>
      <h1 className="m-0 text-2xl font-extrabold text-foreground">Desafio ao Vivo</h1>
      <p className="m-0 text-center text-sm text-neutral">Digite o código de acesso para entrar na sala.</p>
    </header>
  );

  const codeInput = (
    <input
      id="access-code"
      type="text"
      inputMode="text"
      autoComplete="off"
      autoCapitalize="characters"
      value={code}
      onChange={e => setCode(e.target.value.toUpperCase())}
      placeholder="ABC123"
      maxLength={10}
      autoFocus
      className="focus:ring-primary/20 w-full rounded-lg border-2 border-solid border-primary-lightest bg-bright p-4 text-center font-mono text-3xl font-bold uppercase tracking-[0.4em] text-primary-dark placeholder:font-normal placeholder:tracking-[0.2em] placeholder:text-neutral-light hover:border-primary-light focus:border-primary focus:bg-white focus:outline-none focus:ring-2"
    />
  );

  const submitButton = playful ? (
    <button
      type="submit"
      disabled={loading || !code.trim()}
      className="group/btn relative w-full overflow-hidden rounded-full py-3.5 text-base font-extrabold text-white transition-transform duration-100 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50"
      style={{
        background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-primary) 88%, #fff) 0%, var(--color-primary) 100%)',
        boxShadow: '0 4px 0 color-mix(in srgb, var(--color-primary) 62%, #000), 0 6px 14px rgba(0,0,0,0.18)'
      }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
      />
      <span className="relative">{loading ? 'Entrando...' : 'Bora! ⚡'}</span>
    </button>
  ) : (
    <Button
      type="submit"
      variant="default"
      size="lg"
      fullWidth
      disabled={loading || !code.trim()}
    >
      {loading ? 'Entrando...' : 'Entrar'}
    </Button>
  );

  // Playful: card colorido com faixa de cabeçalho (gradiente primary) que deixa
  // claro que aqui é a entrada de um NOVO desafio, sobre um fundo branco pros campos.
  const joinCard = playful ? (
    <div className="ring-primary/10 w-full overflow-hidden rounded-[24px] bg-white shadow-card ring-1">
      {/* Gradiente primary→primary-escuro (não clareia com branco) p/ o texto
          branco manter contraste em toda a faixa. */}
      <div
        className="flex items-center gap-3 px-5 py-4 text-white"
        style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, color-mix(in srgb, var(--color-primary) 78%, #000) 100%)' }}
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/25">
          <FiZap className="h-6 w-6" />
        </div>
        <div className="min-w-0">
          <h2 className="m-0 text-base font-extrabold leading-tight text-white">Entrar em um novo desafio</h2>
          <p className="m-0 text-xs font-semibold text-white/95">Digite o código que o professor mostrar</p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 p-5"
      >
        <label
          htmlFor="access-code"
          className="text-xs font-bold uppercase tracking-wide text-neutral-dark"
        >
          Código de acesso
        </label>
        {codeInput}
        {submitButton}
        <p className="mt-1 text-center text-xs font-medium text-neutral">Ou escaneie o QR code mostrado no telão 📱</p>
      </form>
    </div>
  ) : (
    <div className="w-full max-w-md">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 rounded-2xl bg-white p-6 shadow-dropshadow-lg"
      >
        <label
          htmlFor="access-code"
          className="text-sm font-semibold text-foreground"
        >
          Código de acesso
        </label>
        {codeInput}
        {submitButton}
      </form>

      <p className="mt-6 text-center text-xs text-neutral-light">Peça o código ao professor ou escaneie o QR code mostrado no telão.</p>

      <div className="mt-4 text-center">
        <Button
          type="button"
          variant="link"
          onClick={() => navigate(PATHS.LIVE_CHALLENGE.MY_CHALLENGES)}
        >
          Meus desafios
        </Button>
      </div>
    </div>
  );

  // Infantil: ganha o chrome (header + bottom bar) + banner da T.I.A explicando o desafio.
  if (playful) {
    return (
      <>
        <Header />
        {/* Mobile: breadcrumb em fluxo logo abaixo do header compacto (pt-72 limpa
            os 68px do header). Desktop: breadcrumb vira fixed (classes md:) e a
            T.I.A absoluta estoura o topo do card curto → sm:pt-44 dá headroom. */}
        <main className="min-h-screen bg-neutral-bright px-4 pb-36 pt-[72px] sm:pt-44 md:px-11">
          <Breadcrumbs data={{ title: 'Avaliações e Questões', nav: [{ name: 'Desafio ao Vivo', isActive: true }] }} />
          <div
            className="relative mb-8 mt-4 flex items-end gap-2 rounded-[24px] p-4 shadow-card sm:mt-0 sm:block sm:pl-[150px]"
            style={{ background: 'var(--color-bg-lilac)' }}
          >
            <img
              src={tiaUau}
              alt="T.I.A"
              className="pointer-events-none z-10 h-[175px] w-auto shrink-0 self-end object-contain object-bottom max-[374px]:h-[140px] sm:absolute sm:bottom-0 sm:left-2 sm:h-[180px]"
            />
            <div className="relative z-10 min-w-0 flex-1 rounded-[16px] rounded-bl-none bg-white p-4 shadow-card sm:max-w-[700px]">
              <span
                className="block text-xs font-extrabold"
                style={{ color: 'var(--color-secondary-dark)' }}
              >
                T.I.A
              </span>
              <p
                className="mb-0 text-sm font-bold leading-relaxed"
                style={{ color: '#222731' }}
              >
                <TypewriterText text={'Bora pro Desafio ao Vivo? ⚡ Quanto mais rápido e certo, mais pontos. Boa sorte! 🚀'} />
              </p>
            </div>
          </div>
          {/* Full-width como as outras telas: 50/50 no desktop (acesso por código
              à esq., histórico à dir.); empilha no mobile. */}
          <div className="grid items-start gap-6 lg:grid-cols-2">
            <div className="lg:sticky lg:top-44">{joinCard}</div>
            <ParticipationsPanel />
          </div>
        </main>
        <BottomBarInfantil />
      </>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bright px-4 py-12">
      {joinHeaderBlock}
      {joinCard}
    </div>
  );
};

export default JoinPage;
